import { getFirebaseAuthService, getFunctionsService } from './krida-auth.js';
import { httpsCallable } from "https://www.gstatic.com/firebasejs/10.12.2/firebase-functions.js";

const tournamentsListDiv = document.getElementById('tournaments-list');

export async function fetchAndDisplayMyActiveTournaments() {
    if (!tournamentsListDiv) {
        console.warn("My Active Tournaments list div not found.");
        return;
    }

    tournamentsListDiv.innerHTML = '<p>Loading your active tournaments...</p>';

    const auth = getFirebaseAuthService();
    if (!auth || !auth.currentUser) {
        tournamentsListDiv.innerHTML = '<p>Please log in to see your tournaments.</p>';
        return;
    }

    const functions = getFunctionsService();
    if (!functions) {
        console.error("Functions service not available for fetching user's tournaments.");
        tournamentsListDiv.innerHTML = '<p>Error: Could not connect to tournament service.</p>';
        return;
    }

    try {
        const getMyActiveTournamentsFn = httpsCallable(functions, 'getMyActiveTournaments');
        const result = await getMyActiveTournamentsFn();
        const { activeTournaments = [] } = result.data || {};

        if (activeTournaments.length === 0) {
            tournamentsListDiv.innerHTML = '<p>You are not currently in any active tournaments.</p>';
            return;
        }

        const ul = document.createElement('ul');
        ul.className = 'active-tournaments-list';

        activeTournaments.forEach(tournament => {
            const li = document.createElement('li');
            li.className = 'tournament-list-item';
            li.dataset.tournamentId = tournament.id;

            let tournamentStatusText = tournament.status || 'Unknown Status';
            if (tournament.status === 'pending' && tournament.type === 'open') {
                tournamentStatusText = 'Pending (Open for Joins)';
            } else if (tournament.status === 'pending' && tournament.type === 'closed') {
                tournamentStatusText = 'Pending (Invitational)';
            }

            const displayStyle = tournament.style === 'round_robin' ? 'round-robin' : (tournament.style || 'N/A');
            const statusText = tournament.status === 'completed' ? 'Completed' : tournamentStatusText;

            li.innerHTML = `
                <div class="tournament-item-name">${tournament.name || 'Unnamed Tournament'}</div>
                <div class="tournament-item-details">
                    <span>Status: ${statusText}</span>
                    <span>Players: ${tournament.participantsCount || 0}/${tournament.maxPlayers || 'N/A'}</span>
                    <span>Type: ${tournament.type || 'N/A'}, Style: ${displayStyle}</span>
                </div>
            `;

            // For in-progress and completed tournaments, load details (standings, active games, settings)
            if (tournament.status === 'in-progress' || tournament.status === 'completed') {
                _loadTournamentDetails(tournament.id, li, auth.currentUser.uid, tournament);
            }

            // --- View Games button and container ---
            const gamesSection = document.createElement('div');
            gamesSection.className = 'tournament-games-section';
            gamesSection.id = `games-for-${tournament.id}`;

            const viewGamesButton = document.createElement('button');
            viewGamesButton.textContent = 'View Games';
            viewGamesButton.className = 'btn btn-secondary btn-small';
            viewGamesButton.onclick = () => {
                const currentlyVisible = gamesSection.style.display !== 'none';
                if (currentlyVisible && gamesSection.innerHTML !== '') {
                    gamesSection.style.display = 'none';
                    gamesSection.innerHTML = '';
                    viewGamesButton.textContent = 'View Games';
                } else {
                    gamesSection.style.display = 'block';
                    viewGamesButton.textContent = 'Hide Games';
                    fetchAndDisplayTournamentGames(tournament.id, gamesSection, auth.currentUser.uid);
                }
            };

            li.appendChild(viewGamesButton);
            li.appendChild(gamesSection);

            // --- Delete Tournament button for owner ---
            if (tournament.createdBy === auth.currentUser.uid) {
                const deleteButton = document.createElement('button');
                deleteButton.textContent = 'Delete Tournament';
                deleteButton.className = 'btn btn-danger btn-small btn-delete-tournament';
                deleteButton.style.marginLeft = '10px';

                deleteButton.onclick = async () => {
                    if (confirm(`Are you sure you want to delete the tournament "${tournament.name || 'Unnamed Tournament'}"? This action cannot be undone.`)) {
                        try {
                            const deleteTournamentFn = httpsCallable(functions, 'deleteTournament');
                            await deleteTournamentFn({ tournamentId: tournament.id });
                            li.remove();
                            if (tournamentsListDiv.querySelector('.active-tournaments-list') &&
                                tournamentsListDiv.querySelector('.active-tournaments-list').children.length === 0) {
                                tournamentsListDiv.innerHTML = '<p>You are not currently in any active tournaments.</p>';
                            }
                        } catch (error) {
                            console.error("Error deleting tournament:", error);
                            alert(`Failed to delete tournament: ${error.message}`);
                        }
                    }
                };
                li.appendChild(deleteButton);
            }

            ul.appendChild(li);
        });

        tournamentsListDiv.innerHTML = '';
        tournamentsListDiv.appendChild(ul);

    } catch (error) {
        console.error("Error fetching user's active tournaments:", error);
        tournamentsListDiv.innerHTML = `<p>Error loading your tournaments: ${error.message}</p>`;
    }
}

export function refreshMyTournamentsView() {
    fetchAndDisplayMyActiveTournaments();
}

/**
 * Loads full tournament details (games, standings, active matches, settings)
 * and enriches the tournament list item.
 */
async function _loadTournamentDetails(tournamentId, listItemElement, currentUserUid, tournamentSummary) {
    const functions = getFunctionsService();
    if (!functions) return;

    try {
        const getTournamentDetailsAndGamesFn = httpsCallable(functions, 'getTournamentDetailsAndGames');
        const result = await getTournamentDetailsAndGamesFn({ tournamentId });
        const { games = [], tournament } = result.data || {};

        const detailsContainer = document.createElement('div');
        detailsContainer.className = 'tournament-details-container';

        // --- Game settings display (Task 4) ---
        if (tournament && tournament.gameSettings) {
            const settingsDiv = document.createElement('div');
            settingsDiv.className = 'tournament-game-settings';
            const gs = tournament.gameSettings;
            settingsDiv.innerHTML = `<span class="settings-label">Settings:</span> Win at ${gs.winningScore || 150} pts` +
                (gs.bonusClearBoard !== undefined ? `, Board clear +${gs.bonusClearBoard}` : '');
            detailsContainer.appendChild(settingsDiv);
        }

        // --- Round progress display (Task 1.2, 1.3) ---
        const progressDiv = document.createElement('div');
        progressDiv.className = 'tournament-progress';
        const style = tournamentSummary.style === 'round_robin' ? 'round-robin' : (tournamentSummary.style || '');

        if (tournament && tournament.status !== 'completed') {
            if (style === 'bracket') {
                const totalRounds = Math.ceil(Math.log2(tournamentSummary.participantsCount || 2));
                const currentRound = tournament.currentRound || 1;
                progressDiv.textContent = `Round ${currentRound} of ${totalRounds}`;
            } else if (style === 'round-robin') {
                const finishedGames = games.filter(g => g.status === 'finished').length;
                const totalGames = games.length;
                progressDiv.textContent = `${finishedGames} of ${totalGames} games complete`;
            }
        }
        if (progressDiv.textContent) {
            detailsContainer.appendChild(progressDiv);
        }

        // --- Live standings for in-progress tournaments (Task 1.1) ---
        if (tournament && tournament.status === 'in-progress') {
            const standings = _computeLiveStandings(games, tournament);
            if (standings.length > 0) {
                const standingsDiv = _buildStandingsTable(standings, currentUserUid, false);
                detailsContainer.appendChild(standingsDiv);
            }
        }

        // --- Completed tournament results (Task 3) ---
        if (tournament && tournament.status === 'completed' && tournament.tournamentResults) {
            const resultsDiv = _buildCompletionSummary(tournament, games, currentUserUid, style);
            detailsContainer.appendChild(resultsDiv);
        }

        // --- Upcoming matches display (Task 1.4) ---
        const upcomingGames = games.filter(g => g.status === 'scheduled' || g.status === 'active');
        if (upcomingGames.length > 0 && tournament && tournament.status !== 'completed') {
            const upcomingDiv = document.createElement('div');
            upcomingDiv.className = 'tournament-upcoming-matches';
            upcomingDiv.innerHTML = '<strong>Upcoming Matches:</strong>';
            const upcomingList = document.createElement('ul');
            upcomingGames.forEach(game => {
                const opponent = _getOpponentName(game, currentUserUid);
                const li = document.createElement('li');
                li.textContent = `vs ${opponent}` + (game.status === 'active' ? ' (In Progress)' : ' (Scheduled)');
                upcomingList.appendChild(li);
            });
            upcomingDiv.appendChild(upcomingList);
            detailsContainer.appendChild(upcomingDiv);
        }

        // Insert details before the View Games button
        const viewGamesBtn = listItemElement.querySelector('.btn-secondary');
        if (viewGamesBtn) {
            listItemElement.insertBefore(detailsContainer, viewGamesBtn);
        } else {
            listItemElement.appendChild(detailsContainer);
        }

        // --- Resume Match buttons (Task 2.2, 2.3) - prominent placement ---
        const activeGames = games.filter(g => g.status === 'active');
        if (activeGames.length > 0) {
            // Add pending match class to the list item (Task 2.1)
            listItemElement.classList.add('has-pending-match');

            for (const game of activeGames) {
                const opponent = _getOpponentName(game, currentUserUid);
                const resumeBtn = document.createElement('button');
                resumeBtn.className = 'btn btn-primary btn-resume-match';
                resumeBtn.textContent = `Resume vs ${opponent}`;
                resumeBtn.onclick = () => { window.location.href = `/krida.html?gameId=${game.id}`; };

                // Insert at the top of the tournament item, right after the name (Task 2.2)
                const nameDiv = listItemElement.querySelector('.tournament-item-name');
                if (nameDiv && nameDiv.nextSibling) {
                    listItemElement.insertBefore(resumeBtn, nameDiv.nextSibling);
                } else {
                    listItemElement.prepend(resumeBtn);
                }
            }

            // Notify tab badge (Task 2.1)
            _updateTournamentTabBadge(true);
        }
    } catch (error) {
        console.warn(`Could not load details for tournament ${tournamentId}:`, error.message);
    }
}

/**
 * Computes live standings from games array (Task 1.1)
 */
function _computeLiveStandings(games, tournament) {
    const standings = {};

    // Initialize all tournament players
    if (tournament.players) {
        for (const [uid, playerInfo] of Object.entries(tournament.players)) {
            standings[uid] = {
                uid,
                displayName: playerInfo.displayName || 'Unknown',
                wins: 0,
                totalScore: 0
            };
        }
    }

    games.filter(g => g.status === 'finished').forEach(game => {
        // Ensure players are tracked
        [game.player1Id, game.player2Id].forEach(pid => {
            if (pid && !standings[pid]) {
                standings[pid] = { uid: pid, displayName: '', wins: 0, totalScore: 0 };
            }
        });
        if (game.player1Id && standings[game.player1Id]) {
            standings[game.player1Id].displayName = game.player1DisplayName || standings[game.player1Id].displayName;
            standings[game.player1Id].totalScore += game.currentGameState?.player1Score || 0;
        }
        if (game.player2Id && standings[game.player2Id]) {
            standings[game.player2Id].displayName = game.player2DisplayName || standings[game.player2Id].displayName;
            standings[game.player2Id].totalScore += game.currentGameState?.player2Score || 0;
        }
        if (game.winnerId && standings[game.winnerId]) {
            standings[game.winnerId].wins++;
        }
    });

    return Object.values(standings).sort((a, b) => b.wins - a.wins || b.totalScore - a.totalScore);
}

/**
 * Builds a standings table element
 */
function _buildStandingsTable(standings, currentUserUid, showRank) {
    const div = document.createElement('div');
    div.className = 'tournament-standings';
    div.innerHTML = `<strong>${showRank ? 'Final Standings:' : 'Live Standings:'}</strong>`;

    const table = document.createElement('table');
    table.className = 'standings-table';
    table.innerHTML = `
        <thead>
            <tr>
                <th>#</th>
                <th>Player</th>
                <th>Wins</th>
                <th>Score</th>
            </tr>
        </thead>
    `;
    const tbody = document.createElement('tbody');

    standings.forEach((entry, index) => {
        const tr = document.createElement('tr');
        if (entry.uid === currentUserUid) {
            tr.className = 'standings-current-user';
        }
        const rank = entry.rank || (index + 1);
        let nameCell = entry.displayName;
        if (showRank && rank === 1) {
            nameCell = `<span class="winner-badge">1st</span> ${entry.displayName}`;
        }
        tr.innerHTML = `
            <td>${rank}</td>
            <td>${nameCell}</td>
            <td>${entry.wins}</td>
            <td>${entry.totalScore}</td>
        `;
        tbody.appendChild(tr);
    });

    table.appendChild(tbody);
    div.appendChild(table);
    return div;
}

/**
 * Builds a completion summary for finished tournaments (Task 3)
 */
function _buildCompletionSummary(tournament, games, currentUserUid, style) {
    const div = document.createElement('div');
    div.className = 'tournament-completion-summary';

    // Winner display (Task 3.3)
    const results = tournament.tournamentResults;
    if (results && results.length > 0) {
        const winner = results[0];
        const winnerDiv = document.createElement('div');
        winnerDiv.className = 'tournament-winner';
        winnerDiv.innerHTML = `<span class="winner-badge">1st</span> Winner: <strong>${winner.displayName}</strong>`;
        div.appendChild(winnerDiv);
    }

    // Standings table (Task 3.1, 3.2)
    if (results) {
        const standingsDiv = _buildStandingsTable(results, currentUserUid, true);
        div.appendChild(standingsDiv);
    }

    // Match results (Task 3.4, 3.5)
    if (games && games.length > 0) {
        const matchesDiv = document.createElement('div');
        matchesDiv.className = 'tournament-match-results';
        matchesDiv.innerHTML = '<strong>Match Results:</strong>';

        if (style === 'bracket') {
            // Group games by round (Task 3.5)
            const rounds = {};
            games.filter(g => g.status === 'finished').forEach(g => {
                const round = g.round || 0;
                if (!rounds[round]) rounds[round] = [];
                rounds[round].push(g);
            });
            Object.keys(rounds).sort((a, b) => Number(a) - Number(b)).forEach(round => {
                const roundHeader = document.createElement('div');
                roundHeader.className = 'match-round-header';
                roundHeader.textContent = `Round ${Number(round) + 1}`;
                matchesDiv.appendChild(roundHeader);
                rounds[round].forEach(game => {
                    matchesDiv.appendChild(_buildMatchResultItem(game));
                });
            });
        } else {
            games.filter(g => g.status === 'finished').forEach(game => {
                matchesDiv.appendChild(_buildMatchResultItem(game));
            });
        }
        div.appendChild(matchesDiv);
    }

    return div;
}

/**
 * Builds a single match result display element
 */
function _buildMatchResultItem(game) {
    const item = document.createElement('div');
    item.className = 'match-result-item';
    const p1Score = game.currentGameState?.player1Score || 0;
    const p2Score = game.currentGameState?.player2Score || 0;
    const p1Name = game.player1DisplayName || 'Player 1';
    const p2Name = game.player2DisplayName || 'Player 2';
    const p1Won = game.winnerId === game.player1Id;
    const p2Won = game.winnerId === game.player2Id;
    item.innerHTML = `<span class="${p1Won ? 'match-winner' : ''}">${p1Name} (${p1Score})</span>` +
        ` vs ` +
        `<span class="${p2Won ? 'match-winner' : ''}">${p2Name} (${p2Score})</span>`;
    return item;
}

/**
 * Gets opponent name from a game for the current user
 */
function _getOpponentName(game, currentUserUid) {
    if (game.player1Id === currentUserUid) {
        return game.player2DisplayName || 'Opponent';
    }
    return game.player1DisplayName || 'Opponent';
}

/**
 * Updates the Tournaments tab button with a badge when there are pending matches (Task 2.1)
 */
function _updateTournamentTabBadge(hasPending) {
    const tabButton = document.querySelector('[data-tab="tournaments-panel"]');
    if (!tabButton) return;
    if (hasPending) {
        tabButton.classList.add('has-pending-match');
    } else {
        tabButton.classList.remove('has-pending-match');
    }
}

// --- Fetch and display games for a specific tournament ---
async function fetchAndDisplayTournamentGames(tournamentId, containerElement, currentUserUid) {
    if (!containerElement) {
        console.error("Games container element not provided for tournament", tournamentId);
        return;
    }
    containerElement.innerHTML = '<p class="loading-text">Loading games...</p>';

    const functions = getFunctionsService();
    if (!functions) {
        containerElement.innerHTML = '<p class="error-text">Error: Could not connect to game service.</p>';
        return;
    }

    try {
        const getTournamentDetailsAndGamesFn = httpsCallable(functions, 'getTournamentDetailsAndGames');
        const result = await getTournamentDetailsAndGamesFn({ tournamentId: tournamentId });
        const { games = [] } = result.data || {};

        if (games.length === 0) {
            containerElement.innerHTML = '<p>No games found for this tournament yet.</p>';
            return;
        }

        const gamesUl = document.createElement('ul');
        gamesUl.className = 'tournament-games-list';

        games.forEach(game => {
            const gameLi = createTournamentGameListItem(game, currentUserUid);
            gamesUl.appendChild(gameLi);
        });

        containerElement.innerHTML = '';
        containerElement.appendChild(gamesUl);

    } catch (error) {
        console.error(`Error fetching games for tournament ${tournamentId}:`, error);
        containerElement.innerHTML = `<p class="error-text">Error loading games: ${error.message}</p>`;
    }
}

function createTournamentGameListItem(game, currentUserUid) {
    const gameLi = document.createElement('li');
    gameLi.className = 'tournament-game-item';

    let opponentDisplayName = 'N/A';

    if (game.player1Id === currentUserUid) {
        opponentDisplayName = game.player2DisplayName || 'Computer/TBD';
    } else if (game.player2Id === currentUserUid) {
        opponentDisplayName = game.player1DisplayName || 'Computer/TBD';
    } else {
        opponentDisplayName = `${game.player1DisplayName} vs ${game.player2DisplayName}`;
    }

    const p1Score = game.currentGameState?.player1Score ?? 0;
    const p2Score = game.currentGameState?.player2Score ?? 0;

    let userScore = 0;
    let oppScore = 0;
    if (game.player1Id === currentUserUid) {
        userScore = p1Score;
        oppScore = p2Score;
    } else {
        userScore = p2Score;
        oppScore = p1Score;
    }
    const scoreText = (game.status === 'active' || game.status === 'finished') ? `(You ${userScore} - ${oppScore} Opponent)` : '';

    let gameStatusDisplay = game.status || 'Unknown';
    if (game.status === 'scheduled' && game.scheduledStartTime) {
        const startTime = new Date(game.scheduledStartTime._seconds * 1000);
        gameStatusDisplay = `Scheduled: ${startTime.toLocaleDateString()} ${startTime.toLocaleTimeString()}`;
    }

    gameLi.innerHTML = `
        <div class="game-item-opponent">vs ${opponentDisplayName} ${scoreText}</div>
        <div class="game-item-details">
            <span>Round: ${game.round !== undefined ? game.round : 'N/A'}</span>
            <span>Status: ${gameStatusDisplay}</span>
        </div>
    `;

    const playButton = document.createElement('button');
    playButton.className = 'btn btn-primary btn-small';

    const now = new Date();
    let canPlay = false;
    let buttonText = 'View';

    if (game.status === 'scheduled') {
        const startTime = game.scheduledStartTime ? new Date(game.scheduledStartTime._seconds * 1000 + (game.scheduledStartTime._nanoseconds || 0) / 1000000) : null;
        const endTime = game.scheduledEndTime ? new Date(game.scheduledEndTime._seconds * 1000 + (game.scheduledEndTime._nanoseconds || 0) / 1000000) : null;

        buttonText = `Scheduled (Starts ${startTime ? startTime.toLocaleTimeString() : 'TBD'})`;
        playButton.disabled = true;

        if (startTime && now >= startTime) {
            if (endTime && now > endTime) {
                buttonText = 'Game Ended';
                playButton.disabled = true;
            } else {
                canPlay = true;
                buttonText = 'Play Now';
                playButton.disabled = false;
            }
        } else if (!startTime) {
             buttonText = 'Pending Schedule';
             playButton.disabled = true;
        }
    } else if (game.status === 'active') {
        canPlay = true;
        buttonText = 'Play';
        playButton.disabled = false;
    } else if (game.status === 'finished') {
        buttonText = 'View Result';
        playButton.disabled = false;
    } else {
        playButton.disabled = true;
        buttonText = game.status ? game.status.charAt(0).toUpperCase() + game.status.slice(1) : 'View';
    }

    playButton.textContent = buttonText;
    if (canPlay || game.status === 'finished') {
        playButton.onclick = () => { window.location.href = `/krida.html?gameId=${game.id}`; };
    }

    const actionContainer = document.createElement('div');
    actionContainer.className = 'game-item-actions';
    actionContainer.appendChild(playButton);
    gameLi.appendChild(actionContainer);

    return gameLi;
}
